import { ArrowRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { listAds } from '../services/adService';
import { CATEGORIES } from '../data';
// import { useLocation } from '../hooks/useLocation';

const CATEGORY_IMAGES: Record<string, string> = {
  'Cars & Vehicles': '/assets/cars-and-vehicles.webp',
  Property: '/assets/property.webp',
  Mobiles: '/assets/mobile.webp',
  Jobs: '/assets/jobs.webp',
  Bikes: '/assets/bikes.webp',
  Electronics: '/assets/electronics.webp',
  Fashion: '/assets/fashion.webp',
  Furniture: '/assets/pexels-quang-nguyen-vinh-222549-14023300.webp',
};

/** Map display names to backend category slugs for `/api/ads/?category=<slug>`. */
const CATEGORY_SLUG_MAP: Record<string, string> = {
  'Cars & Vehicles': 'cars-vehicles',
  Property: 'property',
  Mobiles: 'mobile-phones',
  Jobs: 'jobs',
  Bikes: 'motorbikes-scooters',
  Electronics: 'electronics',
  Fashion: 'fashion',
  Furniture: 'furniture',
};

export default function CategoriesSection(): JSX.Element {
  // const { country, isLoading } = useLocation();
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    const entries = CATEGORIES.map((c) => ({
      name: c.name,
      slug: CATEGORY_SLUG_MAP[c.name] ?? c.name.toLowerCase().replace(/\s*&\s*/g, '-').replace(/\s+/g, '-'),
    }));

    const fetchCounts = async () => {
      try {
        const results = await Promise.all(
          entries.map(async ({ name, slug }) => {
            try {
              // Use slug to avoid waiting for category UUID resolution; backend accepts slug or UUID.
              const data = await listAds({ category: slug, page: 1, limit: 1 });
              return [name, data.total] as const;
            } catch {
              return [name, 0] as const;
            }
          })
        );
        if (!cancelled) setCounts(Object.fromEntries(results));
      } catch {
        // Ignore errors; fallback counts remain
      }
    };

    void fetchCounts();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section aria-labelledby="categories-heading" className="py-16 relative">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary-100/50 to-accent/30 rounded-full blur-3xl -z-10" />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 text-primary-600 text-sm font-semibold mb-2">
            <TrendingUp className="w-4 h-4" />
            <span>Popular Categories</span>
          </div>
          <h2
            id="categories-heading"
            className="section-title text-3xl sm:text-4xl font-bold text-dark-900 tracking-tight"
          >
            Browse categories
          </h2>
          {/* <p className="text-dark-500 mt-2 text-lg">Explore thousands of listings worldwide</p> */}
          {/* <p className="text-dark-500 mt-2 text-lg">
            {isLoading 
              ? 'Loading location...' 
              : country 
                ? `Explore thousands of listings in ${country}` 
                : 'Explore thousands of listings worldwide'
            }
          </p> */}
        </div>
        <Link
          to="/categories"
          className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 hover:bg-primary-100 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all group fade-up"
          style={{ animationDelay: '0.1s' }}
        >
          See all
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      <ul
        className="categories-grid grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 list-none"
        role="list"
      >
        {CATEGORIES.map((cat, index) => {
          const Card = (
            <Link
              to={`/category/${CATEGORY_SLUG_MAP[cat.name]}`}
              className="group relative block overflow-hidden rounded-2xl border border-dark-100 bg-white transition-all duration-300 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-100/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
              aria-label={`Browse ${cat.name} — ${(counts[cat.name] ?? cat.count)} ads`}
            >
              <div
                className="h-28 relative overflow-hidden bg-dark-100"
                style={{
                  backgroundImage: `url("${CATEGORY_IMAGES[cat.name] ?? 'https://picsum.photos/seed/bazaar-shopping/900/560'}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                aria-hidden="true"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/45 to-dark-900/10" />
               
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-dark-900/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="p-4">
                <div className="font-semibold text-dark-900 text-base group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </div>
                <div className="text-sm text-dark-500 mt-1">
                  {(counts[cat.name] ?? cat.count)} ads
                </div>
              </div>

              <ArrowRight className="absolute bottom-4 right-4 w-5 h-5 text-primary-500 opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
            </Link>
          );

          return (
            <li key={cat.name} style={{ animationDelay: `${index * 0.08}s` }} className="fade-up">
              {Card}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
